import { PORTAL_ROUTES } from 'utils/constants';
import { Contact, FilePen, ShieldCheck, SlidersHorizontal, Stethoscope } from 'lucide-react';

export const PROFILE_STEPS = [
  { id: 'personal', navLabel: 'Personal', title: 'Personal Information', subtitle: 'Name, address & emergency contact', icon: Contact, iconBg: 'bg-blue-100', iconColor: 'text-blue-500' },
  { id: 'insurance', navLabel: 'Insurance', title: 'Insurance', subtitle: 'Carrier, member details & card', icon: ShieldCheck, iconBg: 'bg-green-100', iconColor: 'text-green-600', optional: true },
  { id: 'medical', navLabel: 'Medical', title: 'Medical History', subtitle: 'Allergies, conditions & medications', icon: Stethoscope, iconBg: 'bg-purple-100', iconColor: 'text-purple-500' },
  { id: 'consent', navLabel: 'Consent', title: 'Consent Forms', subtitle: 'Review & agree to required forms', icon: FilePen, iconBg: 'bg-amber-100', iconColor: 'text-amber-600' },
  { id: 'preferences', navLabel: 'Preferences', title: 'Preferences', subtitle: 'Appointment & contact preferences', icon: SlidersHorizontal, iconBg: 'bg-indigo-100', iconColor: 'text-indigo-500' },
];

export const stepRoute = (step) => `${PORTAL_ROUTES.PROFILE}/${step.id}`;

export const getStepIndex = (id) => PROFILE_STEPS.findIndex((s) => s.id === id);
export const getStepById = (id) => PROFILE_STEPS.find((s) => s.id === id);

export const getNextStep = (id) => {
  const i = getStepIndex(id);
  return i >= 0 && i < PROFILE_STEPS.length - 1 ? PROFILE_STEPS[i + 1] : null;
};

export const getPrevStep = (id) => {
  const i = getStepIndex(id);
  return i > 0 ? PROFILE_STEPS[i - 1] : null;
};

export const isAllComplete = (completion = {}) => PROFILE_STEPS.every((s) => s.optional || completion[s.id]);

export const getFirstIncompleteStep = (completion = {}) =>
  PROFILE_STEPS.find((s) => !completion[s.id]) ?? PROFILE_STEPS[0];

export const canOpenStep = (index, completion = {}) => {
  if (index <= 0) return true;
  if (completion[PROFILE_STEPS[index]?.id]) return true;

  const gate = PROFILE_STEPS.slice(0, index).reverse().find((s) => !s.optional);
  return !gate || !!completion[gate.id];
};

function isPersonalInfoComplete(attrs) {
  if (!attrs) return false;
  const { first_name, last_name, address_line1, city, state, zip_code } = attrs;
  return [first_name, last_name, address_line1, city, state, zip_code].every(f => !!f?.trim());
}

function isMedicalHistoryComplete(attrs) {
  if (!attrs) return false;
  const { have_no_medical_conditions, allergies, conditions, medications, medical_notes } = attrs;
  if (have_no_medical_conditions) return true;
  const groups = [allergies, conditions, medications];
  const hasAnyChecked = groups.some(g => g?.data?.some(({ attributes: a }) => a.status));
  return hasAnyChecked || !!medical_notes?.trim();
}

function isInsuranceComplete(attrs) {
  if (!attrs) return false;
  if (!attrs.active) return true;

  const { policy_holder, member_id, card_front_pic } = attrs;
  const insurance_company_id = attrs.insurance_company?.data?.id;

  const hasTypedDetails = [policy_holder, insurance_company_id, member_id].every(f => !!f?.trim());

  return hasTypedDetails || !!card_front_pic;
}

function isConsentComplete(consentForms) {
  if (!consentForms?.length) return false;
  return consentForms.every(({ attributes }) => attributes?.status);
}

function isPreferencesComplete(attrs) {
  const { preferences_submitted } = attrs || {};
  return !!preferences_submitted;
}

export function getProfileCompletion(profileAttributes) {
  const { personal_information, medical_history, insurance_intake, consent_forms, preferences } = profileAttributes || {};
  return {
    personal: isPersonalInfoComplete(personal_information?.data?.attributes),
    medical: isMedicalHistoryComplete(medical_history?.data?.attributes),
    insurance: isInsuranceComplete(insurance_intake?.data?.attributes),
    consent: isConsentComplete(consent_forms?.data),
    preferences: isPreferencesComplete(preferences?.data?.attributes),
  };
}
